import type { ReactNode } from 'react'
import type { PermissionModule } from '@/types'
import { usePermissions } from '@/hooks/usePermissions'
import type { ModuleAction } from '@/lib/permissions'

interface PermitProps {
  module: PermissionModule
  action?: ModuleAction
  /** Alternate: exact Spatie permission name (e.g. `clients.delete`). */
  permission?: string
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Conditionally render children when the user has the required module ability.
 *
 * @example
 * <Permit module="clients" action="edit"><Button>Create</Button></Permit>
 * <Permit module="clients" action="delete"><Button>Delete</Button></Permit>
 */
export function Permit({
  module,
  action = 'view',
  permission,
  children,
  fallback = null,
}: PermitProps) {
  const { can, has } = usePermissions()
  const allowed = permission ? has(permission) : can(module, action)

  if (!allowed) return <>{fallback}</>
  return <>{children}</>
}

export default Permit
